<template>
  <div class="kpt-remote-create">
    <vue-headful title="Create Remote" />
    <header class="kpt-detail-pane-header">
      <h4>Create Remote</h4>
      <div class="page-control">
        <font-awesome-icon :icon="['far', 'times-circle']" size="lg" @click="close" />
      </div>
    </header>
    <div class="kpt-create-steps">
      <b-tabs v-model="activeStep">
        <b-tab-item label="Details">
          <p>Creating a remote allows you to add Kymeta's connectivity services to your satellite terminal. In order to get online first create a remote, then add a subscription.</p>
          <br />
          <label class="label is-size-5">Details</label>
          <div class="kpt-field-container">
            <b-field label="Name" horizontal required left>
              <b-input type="text" spellcheck="false" autocomplete="off" v-model="remoteToCreate.name"></b-input>
            </b-field>
            <b-field label="Account" horizontal required left>
              <b-select v-model="remoteToCreate.accountId" v-if="accounts.length > 1" expanded>
                <option
                    v-for="option in accounts"
                    :value="option.id"
                    :key="option.id">
                    {{ option.name }}
                </option>
              </b-select>
              <b-select v-model="remoteToCreate.accountId" v-else>
                <option :value="user.accountId">{{user.accountName}}</option>
              </b-select>
            </b-field>
            <b-field label="Description" horizontal left>
              <b-input type="textarea" spellcheck="false" maxlength="999" rows="10" custom-class="kpt-remote-description-textarea" v-model="remoteToCreate.description"></b-input>
            </b-field>
            <restricted-wrapper label="Kymeta Admin" type="is-warning" v-if="$hasRole('Super User,Kymeta Admin,Sales Support')">
              <b-field label="Billed Account" horizontal required left>
                <b-select v-model="remoteToCreate.billedAccountId" v-if="accounts.length > 1" expanded>
                  <option
                      v-for="option in accounts"
                      :value="option.id"
                      :key="option.id">
                      {{ option.name }}
                  </option>
                </b-select>
              </b-field>
            </restricted-wrapper>
          </div>
        </b-tab-item>
        <b-tab-item label="Devices">
          <p>Remotes must be aware of models and serial numbers of some of the hardware that is attached to your terminal in order to bring the terminal online on the Kymeta satellite and cellular networks.</p>
          <p>Remotes can also be used to track the hardware you have attached to the terminal.</p>
          <br />
          <label class="label is-size-5">Satellite Devices</label>
          <table class="table">
            <thead>
              <tr>
                <th style="width: 150px">Device</th>
                <th style="width: 400px">Model</th>
                <th style="width: 300px">Serial / ID</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><b-field label="Terminal"></b-field></td>
                <td style="width: 400px">
                  <b-select v-model="remoteToCreate.terminalModel" expanded>
                    <option
                        v-for="option in getModelsForType('DEV_TRMNL')"
                        :value="option.shortcode"
                        :key="option.shortcode">
                        {{ option.name }}
                    </option>
                  </b-select>
                </td>
                <td><b-input type="text" v-model="remoteToCreate.terminalSerial"></b-input></td>
              </tr>
              <tr>
                <td><b-field label="Antenna" :required="modelIsRequired(remoteToCreate.antennaSerial)"></b-field></td>
                <td>
                  <b-select v-model="remoteToCreate.antennaModel" expanded>
                    <option
                        v-for="option in getModelsForType('DEV_ANTNA')"
                        :value="option.shortcode"
                        :key="option.shortcode">
                        {{ option.name }}
                    </option>
                  </b-select>
                </td>
                <td><b-input type="text" v-model="remoteToCreate.antennaSerial"></b-input></td>
              </tr>
              <tr>
                <td><b-field label="Modem" :required="modelIsRequired(remoteToCreate.modemSerial)"></b-field></td>
                <td>
                  <b-select v-model="remoteToCreate.modemModel" expanded>
                    <option
                        v-for="option in getModelsForType('DEV_MODEM')"
                        :value="option.shortcode"
                        :key="option.shortcode">
                        {{ option.name }}
                    </option>
                  </b-select>
                </td>
                <td><b-input type="text" v-model="remoteToCreate.modemSerial"></b-input></td>
              </tr>
              <tr>
                <td><b-field label="BUC"></b-field></td>
                <td>
                  <b-select v-model="remoteToCreate.bucModel" expanded>
                    <option
                        v-for="option in getModelsForType('DEV_BUC')"
                        :value="option.shortcode"
                        :key="option.shortcode">
                        {{ option.name }}
                    </option>
                  </b-select>
                </td>
                <td><b-input type="text" v-model="remoteToCreate.bucSerial"></b-input></td>
              </tr>
              <tr>
                <td><b-field label="LNB"></b-field></td>
                <td>
                  <b-select v-model="remoteToCreate.lnbModel" expanded>
                    <option
                        v-for="option in getModelsForType('DEV_LNB')"
                        :value="option.shortcode"
                        :key="option.shortcode">
                        {{ option.name }}
                    </option>
                  </b-select>
                </td>
                <td><b-input type="text" v-model="remoteToCreate.lnbSerial"></b-input></td>
              </tr>
            </tbody>
          </table>
          <label class="label is-size-5">Cellular Devices</label>
          <table class="table">
            <thead>
              <tr>
                <th style="width: 150px">Device</th>
                <th style="width: 400px">Model</th>
                <th style="width: 300px">Serial / ID</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Cell Router</td>
                <td style="width: 400px"><b-select v-model="remoteToCreate.cellRouterModel" expanded>
                    <option
                        v-for="option in getModelsForType('DEV_CLRTR')"
                        :value="option.shortcode"
                        :key="option.shortcode">
                        {{ option.name }}
                    </option>
                  </b-select></td>
                <td><b-input type="text" v-model="remoteToCreate.cellRouterSerial" maxlength="14" v-cleave="cellRouterPattern.custom" PlaceHolder="ex: XXXX-XXXX-XXXX, X is a Hex digit" @blur="blurEventHandlerCellRouterSerial($event)"></b-input></td>
              </tr>
              <tr>
                <td>Cell Router IMEI</td>
                <td><b-input type="text" disabled placeholder="Not available"></b-input></td>
                <td><b-input type="text" v-model="remoteToCreate.cellRouterImei"></b-input></td>
              </tr>
              <tr>
                <td>ICCID (SIM ID)</td>
                <td><b-input type="text" disabled placeholder="Not available"></b-input></td>
                <td><b-input type="text" v-model="remoteToCreate.cellRouterIccid"></b-input></td>
              </tr>
            </tbody>
          </table>
        </b-tab-item>
        <b-tab-item label="Confirmation">
          <b-notification type="is-danger" role="alert" :closable="false" icon-pack="far" :active="isInvalid">
            <p><strong>Invalid field<template v-if="validationErrors.length > 1">s</template></strong>: <span v-for="(err, idx) in validationErrors" :key="idx">{{err}}<template v-if="idx != validationErrors.length - 1">, </template></span></p>
          </b-notification>
          <div class="kpt-remote-create-confirmation">
            <h6 class="is-uppercase" style="margin-bottom: 8px;"><strong>Details</strong></h6>
            <b-field label="Name" horizontal left>
              {{remoteToCreate.name}}
            </b-field>
            <b-field label="Account" horizontal left>
              {{getAccountName(remoteToCreate.accountId)}}
            </b-field>
            <b-field label="Description" horizontal left>
              {{remoteToCreate.description || '-'}}
            </b-field>
            <h6 class="has-text-gold is-uppercase" style="margin: 8px 0;"><strong>Satellite Devices</strong></h6>
            <b-field label="Terminal" horizontal left>
              <div v-if="remoteToCreate.terminalModel || remoteToCreate.terminalSerial">
                <span v-if="remoteToCreate.terminalModel">{{idToName(remoteToCreate.terminalModel, $store.state.devices.models)}}</span> <i v-if="remoteToCreate.terminalSerial">(SN: {{remoteToCreate.terminalSerial || '-'}})</i>  
              </div>
              <span v-else>-</span>
            </b-field>
            <b-field label="Antenna" horizontal left>
              <div v-if="remoteToCreate.antennaModel || remoteToCreate.antennaSerial">
                <span v-if="remoteToCreate.antennaModel">{{idToName(remoteToCreate.antennaModel, $store.state.devices.models)}}</span> <i v-if="remoteToCreate.antennaSerial">(SN: {{remoteToCreate.antennaSerial || '-'}})</i>  
              </div>
              <span v-else>-</span>
            </b-field>
            <b-field label="Modem" horizontal left>
              <div v-if="remoteToCreate.modemModel || remoteToCreate.modemSerial">
                <span v-if="remoteToCreate.modemModel">{{idToName(remoteToCreate.modemModel, $store.state.devices.models)}}</span> <i v-if="remoteToCreate.modemSerial">(SN: {{remoteToCreate.modemSerial || '-'}})</i>  
              </div>
              <span v-else>-</span>
            </b-field>
            <b-field label="BUC" horizontal left>
              <div v-if="remoteToCreate.bucModel || remoteToCreate.bucSerial">
                <span v-if="remoteToCreate.bucModel">{{idToName(remoteToCreate.bucModel, $store.state.devices.models)}}</span> <i v-if="remoteToCreate.bucSerial">(SN: {{remoteToCreate.bucSerial || '-'}})</i>  
              </div>
              <span v-else>-</span>
            </b-field>
            <b-field label="LNB" horizontal left>
              <div v-if="remoteToCreate.lnbModel || remoteToCreate.lnbSerial">
                <span v-if="remoteToCreate.lnbModel">{{idToName(remoteToCreate.lnbModel, $store.state.devices.models)}}</span> <i v-if="remoteToCreate.lnbSerial">(SN: {{remoteToCreate.lnbSerial || '-'}})</i>  
              </div>
              <span v-else>-</span>
            </b-field>
            <h6 class="has-text-gold is-uppercase" style="margin: 8px 0;"><strong>Cellular Devices</strong></h6>
            <b-field label="Router" horizontal left>
              <div v-if="remoteToCreate.cellRouterModel || remoteToCreate.cellRouterSerial">
                <span v-if="remoteToCreate.cellRouterModel">{{idToName(remoteToCreate.cellRouterModel, $store.state.devices.models)}}</span> <i v-if="remoteToCreate.cellRouterSerial">(SN: {{remoteToCreate.cellRouterSerial || '-'}})</i>  
              </div>
              <span v-else>-</span>
            </b-field>
            <b-field label="IMEI" horizontal left>
              <div v-if="remoteToCreate.cellRouterImei">
                <i v-if="remoteToCreate.cellRouterImei">SN: {{remoteToCreate.cellRouterImei || '-'}}</i>  
              </div>
              <span v-else>-</span>
            </b-field>
            <b-field label="ICCID (SIM ID)" horizontal left>
              <div v-if="remoteToCreate.cellRouterIccid">
                <i v-if="remoteToCreate.cellRouterIccid">SN: {{remoteToCreate.cellRouterIccid || '-'}}</i>  
              </div>
              <span v-else>-</span>
            </b-field>
          </div>
        </b-tab-item>
      </b-tabs>
    </div>
    <footer class="kpt-create-navigation">
      <b-notification
        :active="hasError"
          type="is-danger"
          aria-close-label="Close notification"
          role="alert"
          :closable="false">
          {{devicesError}}
      </b-notification>
      <div class="buttons">
        <b-button size="is-small" type="is-primary" @click="create" v-if="activeStep == 2" :disabled="isInvalid || creating" :loading="creating">Create Remote</b-button>
        <b-button size="is-small" type="is-primary" @click="activeStep = 2" v-if="activeStep != 2">Review & Create</b-button>
        <b-button size="is-small" icon-left="chevron-left" @click="activeStep--" :disabled="activeStep == 0">Previous</b-button>
        <b-button size="is-small" icon-right="chevron-right" @click="activeStep++" :disabled="activeStep == 2">Next</b-button>
        <b-button size="is-small" type="is-outlined" @click="cancel" :disabled="creating">Cancel</b-button>
      </div>
    </footer>
  </div>
</template>
<script>
import { orderBy } from 'lodash';
import { idToNameParserMixin } from '../../shared/mixins/idToNameParser';
import RestrictedWrapper from '../../shared/RestrictedWrapperComponent.vue';
import { validateCellRouterSerialMixin } from '../../shared/mixins/validateCellRouterSerialMixin'; // Cleave.js

  export default {
    data() {
      return {
        activeStep: 0,
        remoteToCreate: {
          name: null,
          accountId: null,
          description: null,
          antennaModel: null,
          antennaSerial: null,
          modemModel: null,
          modemSerial: null,
          bucModel: null,
          bucSerial: null,
          lnbModel: null,
          lnbSerial: null,
          terminalModel: null,
          terminalSerial: null
        },
        creating: false,
        validationErrors: [],
        cellRouterPattern: {
          custom: {
            delimiters: ['-', '-'],
            blocks: [4, 4, 4],
            numeral: false,
          }
        }
      }
    },
    mixins: [idToNameParserMixin, validateCellRouterSerialMixin],
    components: {
      RestrictedWrapper
    },
    methods: {
      close() {
        this.$router.push({ path: '/remotes' });
      },
      cancel() {
        this.$store.commit('setRemoteDevicesError', null);
        this.$router.go(-1);
      },
      async create() {
        if (this.validationErrors.length) return;
        this.creating = true;
        const result = await this.$store.dispatch('addRemote', this.remoteToCreate);
        this.creating = false;
        if (result) {
          this.$router.push({ name: 'remoteOverview', params: { id: this.$store.state.remotes.remote.id } });
        }
      },
      validate() {
        this.validationErrors = [];
        if (!this.remoteToCreate.name) this.validationErrors.push('Remote Name');
        if (!this.remoteToCreate.accountId) this.validationErrors.push('Account');
        if (this.remoteToCreate.modemSerial && !this.remoteToCreate.modemModel) this.validationErrors.push('Modem Model');
        if (this.remoteToCreate.antennaSerial && !this.remoteToCreate.antennaModel) this.validationErrors.push('Antenna Model');
        if (this.remoteToCreate.cellRouterSerial && !this.remoteToCreate.cellRouterModel) this.validationErrors.push('Cell Router Model');
        if (this.remoteToCreate.cellRouterSerial && !this.validCellRouterSerial(this.remoteToCreate.cellRouterSerial)) this.validationErrors.push('Cell Router Serial (ex: XXXX-XXXX-XXXX, X is a Hex digit)');
        if (this.$hasRole('Super User,Kymeta Admin,Sales Support') && !this.remoteToCreate.billedAccountId) this.validationErrors.push('Billed Account');
      },
      getModelsForType(type) {
        const deviceTypeId = this.deviceTypes.find(t => t.shortcode == type);
        return this.deviceModels.filter(m => m.deviceTypeId == deviceTypeId.id);
      },
      modelIsRequired(property) {
        // eslint-disable-next-line no-void
        return property != null && property != '' && property != undefined && property != void 0;
      },
      getAccountName(id) {
        const account = this.accounts.find(a => a.id == id);
        return account ? account.name : '-';
      },
      validCellRouterSerial(serial) {
        const re = /^(?:[A-F0-9]{4}-){2}[A-F0-9]{4}$/;
        return re.test(serial);
      },
      blurEventHandlerCellRouterSerial(e) {
        const val = e.target.value;
        this.remoteToCreate.cellRouterSerial = val;
      }
    },
    mounted() {
      this.remoteToCreate.accountId = this.user.accountId;
    },
    computed: {
      user() { return this.$store.state.user.user; },
      isInvalid() { return this.validationErrors.length > 0; },
      accounts() { return orderBy(this.$store.state.accounts.accounts, ['name'], ['asc']); },
      deviceTypes() { return this.$store.state.devices.types; },
      deviceModels() { return this.$store.state.devices.models; },
      devicesError() { return this.$store.state.remotes.remoteDevicesError; },
      hasError() { return this.devicesError != null; }
    },
    watch: {
      // eslint-disable-next-line quote-props, no-unused-vars
      'activeStep': function (newValue, oldValue) {
        this.validationErrors = [];
        if (newValue == 2) {
          this.validate();
        }
      }
    }
  }
</script>
<style lang="scss" scoped>
.kpt-remote-create {
  height: 100%;
  display: flex;
  flex-direction: column;

  .kpt-field-container {
    width: 100%;

    @media screen and (min-width: 1441px) {
      width: 50%;
    }
  }

  .kpt-create-steps {
    flex: 1 1 auto;
    overflow-y: auto;
  }

  .kpt-remote-create-confirmation {
    .field {
      margin-bottom: 2px;
    }
  }
}

</style>
<style lang="scss">
.kpt-remote-description-textarea {
  resize: none;
}
</style>
